import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
	usePoMutation,
	documentUploadQuery,
	useAxiosInstance,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	DefaultUploadParameters,
	useFileUpload,
	useProcessControl,
} from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import { useBoolean } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import {
	FormInstanceNative,
	FormInstanceWeb,
	prepareSubmitData,
} from './MyFilesUploadFormDefinitions';
import type { MojeSouboryNahratContextType } from './context.types';

export const MojeSouboryNahratContext =
	createContext<Nullable<MojeSouboryNahratContextType>>(null);

type MojeSouboryNahratContextProviderProps = {
	children: React.ReactNode;
	code: PageCode;
	isWeb?: boolean;
};

export function MojeSouboryNahratContextProvider({
	children,
	code,
	isWeb,
}: MojeSouboryNahratContextProviderProps) {
	const { t } = useTranslation([code]);
	const { controls, setControls } = useProcessControl();
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const queryClient = useQueryClient();
	const { axiosInstance } = useAxiosInstance();

	const isUploadModalOpened = useBoolean(false);
	const formDefinition = isWeb
		? FormInstanceWeb({ code })
		: FormInstanceNative({ code });

	useEffect(() => {
		if (!isUploadModalOpened.value) {
			formDefinition.formReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUploadModalOpened.value]);

	const uploadMutation = usePoMutation({
		mutationFn: documentUploadQuery(axiosInstance),
		onSuccess: async () => {
			toastMessageSuccess(t('zpravy.dokument-byl-uspesne-ulozen'));
			await queryClient.invalidateQueries({ queryKey: ['uloziste-vyuziti'] });
			await queryClient.invalidateQueries(['documents', code]);
			formDefinition.formReset();
			isUploadModalOpened.setFalse();
		},
	});

	const onSubmit = formDefinition.formMethods.handleSubmit(() => {
		const preparedSubmitData = prepareSubmitData(
			formDefinition.formMethods.getValues()
		);
		if (!preparedSubmitData) {
			toastMessageError(t('zpravy.nahrani-souboru-se-nepovedlo'));
			return;
		}
		return uploadMutation.mutate(preparedSubmitData);
	});

	const fileUploadHook = useFileUpload(
		formDefinition.formMethods,
		'files',
		DefaultUploadParameters
	);

	return (
		<MojeSouboryNahratContext.Provider
			value={{
				code,
				formDefinition,
				controls,
				setControls,
				isUploadModalOpened,
				onSubmit,
				isUploading: uploadMutation.isLoading,
				fileUploadHook,
			}}>
			{children}
		</MojeSouboryNahratContext.Provider>
	);
}

export const useMojeSouboryNahratContext = (): MojeSouboryNahratContextType =>
	useContext(MojeSouboryNahratContext) as MojeSouboryNahratContextType;
